import { useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from '@apollo/client/react/hoc';
import to from 'await-to-js';
import { notify } from 'components/toast';
import { withSpanAsync, SpanType } from '@/tracing';
import mutations from './mutations';
import { RadioButtonsGroup } from './radioButtonsGroup/radioButtonsGroup';
import gql from 'graphql-tag';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { DialogHeader, DialogTitle, DialogDescription } from '@/shadcn/dialog';

const enhanceSubscriptions = (subscriptions, actions) =>
  subscriptions.map(subscription => {
    const foundAction = actions.find(
      action => action.key === subscription.actionkey
    );
    const enhancedSubscription = {
      ...subscription,
      actionTitle: foundAction?.title ?? subscription.actionkey,
      loading: false,
      showSuccess: false
    };
    return enhancedSubscription;
  });

const SubscriptionsMenu = ({
  userId,
  workflowId,
  actions,
  subscriptionsList,
  onCrossClick,
  updateNotificationSubscription
}) => {
  if (subscriptionsList?.loading) {
    return <TraceIconSpinner />;
  }

  const {
    workflows: { nodes }
  } = subscriptionsList;
  const currentWf = nodes[0];
  const subscriptions = currentWf.notificationSubscriptionsUpdated.nodes;

  const [currentSubscriptions, setSubscriptions] = useState(() =>
    enhanceSubscriptions(subscriptions, actions)
  );

  const updateSubscription = (index, newSubscription) => {
    setSubscriptions(prevSubscriptions => {
      const updatedSubscriptions = [...prevSubscriptions];
      updatedSubscriptions[index] = {
        ...prevSubscriptions[index],
        ...newSubscription
      };
      return updatedSubscriptions;
    });
  };

  const executeTransaction = async (subscription, actionkey) => {
    const [err] = await to(
      updateNotificationSubscription({
        variables: {
          userId,
          workflowId,
          actionkey,
          subscription
        }
      })
    );
    return err;
  };

  const handleTabClick = (clickedSubscription, actionkey) => {
    const newSubscription = clickedSubscription.toUpperCase();

    const indexToUpdate = currentSubscriptions.findIndex(
      s => s.actionkey === actionkey
    );

    const prevSubscription = currentSubscriptions[indexToUpdate].subscription;
    if (prevSubscription === newSubscription) {
      return null;
    }

    updateSubscription(indexToUpdate, {
      subscription: newSubscription,
      loading: true
    });

    return withSpanAsync(
      'updateNotificationSubscription',
      SpanType.processing,
      async () => {
        const promise = executeTransaction(newSubscription, actionkey);
        notify.promise(promise, {
          loading: 'Updating notification subscription...',
          success: `Notification subscription updated`,
          error: `Notification subscription update failed`
        });
        const err = await executeTransaction(newSubscription, actionkey);

        if (err) {
          updateSubscription(indexToUpdate, {
            subscription: prevSubscription,
            loading: false
          });
        } else {
          // Faking update in case showSuccess is already true
          updateSubscription(indexToUpdate, {
            subscription: newSubscription,
            loading: false,
            showSuccess: false
          });
          updateSubscription(indexToUpdate, {
            subscription: newSubscription,
            loading: false,
            showSuccess: true
          });
        }
      }
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Notification subscriptions</DialogTitle>
        <DialogDescription className="mb-2">
          <ul className="list-inside list-disc text-xs">
            <li>
              <b>Off:</b> You will never receive any notifications for the given
              action.
            </li>
            <li>
              <b>Default:</b> You will receive notifications as they are
              predefined in the workflow.
            </li>
            <li>
              <b>Always:</b> You will receive all notifications for the given
              action, every time.
            </li>
          </ul>
        </DialogDescription>
        <section className="scrollbar-thin flex max-h-[275px] flex-col flex-nowrap overflow-y-auto text-sm">
          {currentSubscriptions.length === 0 && (
            <p className="text-center text-sm">No subscriptions</p>
          )}

          {currentSubscriptions.map(sub => (
            <div
              key={sub.actionkey}
              className="border-border flex items-center justify-between gap-5 py-2 text-sm"
            >
              <div className="text-sm">{sub.actionTitle}</div>
              <RadioButtonsGroup
                tabs={['Off', 'Custom', 'Always']}
                value={sub.subscription}
                loading={sub.loading}
                onChange={subscription =>
                  handleTabClick(subscription, sub.actionkey)
                }
              />
            </div>
          ))}
        </section>
      </DialogHeader>
    </>
  );
};

SubscriptionsMenu.propTypes = {
  userId: PropTypes.string.isRequired,
  workflowId: PropTypes.string.isRequired,
  subscriptionsList: PropTypes.object.isRequired,
  actions: PropTypes.array.isRequired,
  onCrossClick: PropTypes.func.isRequired,
  updateNotificationSubscription: PropTypes.func.isRequired
};

const GET_NOTIFICATION_SUBSCRIPTIONS = gql`
  query GetNotificationSubscriptionsUpdated($workflowId: BigInt!) {
    workflows(filter: { rowId: { equalTo: $workflowId } }) {
      nodes {
        rowId
        notificationSubscriptionsUpdated {
          nodes {
            actionkey
            rowId
            subscription
          }
        }
      }
    }
  }
`;

export default compose(
  graphql(mutations.updateNotificationSubscription, {
    name: 'updateNotificationSubscription'
  }),
  graphql(GET_NOTIFICATION_SUBSCRIPTIONS, {
    name: 'subscriptionsList',
    options: ({ workflowId }) => ({
      variables: { workflowId }
    })
  })
)(SubscriptionsMenu);
